import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Eye, Lock, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Politique de Confidentialité"
        description="Politique de confidentialité d'Eau Gratuite. Découvrez comment nous protégeons vos données personnelles."
        path="/politique-confidentialite"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Politique de Confidentialité
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Eau Gratuite respecte votre vie privée et protège vos données personnelles
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Dernière mise à jour : 12 septembre 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            
            {/* Collecte des données */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Eye className="h-6 w-6 text-sky-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Collecte des Données
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Nous collectons les informations suivantes lorsque vous nous contactez :
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone (optionnel)</li>
                    <li>Message et sujet de votre demande</li>
                    <li>Type de demande (partenariat, général, support, presse)</li>
                  </ul>
                  <p>
                    Ces données sont collectées uniquement dans le cadre de notre relation commerciale 
                    et pour répondre à vos demandes de renseignements sur nos services publicitaires.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Utilisation des données */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Lock className="h-6 w-6 text-emerald-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Utilisation des Données
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>Vos données personnelles sont utilisées pour :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Répondre à vos demandes de renseignements</li>
                    <li>Vous proposer nos services de publicité sur bouteilles d'eau</li>
                    <li>Établir des devis personnalisés pour vos campagnes</li>
                    <li>Vous tenir informé de nos nouveaux services (avec votre consentement)</li>
                    <li>Respecter nos obligations légales et réglementaires</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Partage des données */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Shield className="h-6 w-6 text-sky-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Partage et Protection
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>
                    <strong>Nous ne vendons jamais vos données personnelles.</strong>
                  </p>
                  <p>
                    Vos données peuvent être partagées uniquement avec :
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>L'Association Ulker's dans le cadre de nos projets de puits d'eau</li>
                    <li>Nos prestataires techniques (hébergement, email) sous contrat de confidentialité</li>
                    <li>Les autorités légales si requis par la loi</li>
                  </ul>
                  <p>
                    Toutes vos données sont stockées de manière sécurisée avec chiffrement 
                    et accès restreint à notre équipe.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Droits */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Mail className="h-6 w-6 text-emerald-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Vos Droits RGPD
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Droit d'accès :</strong> obtenir une copie de vos données</li>
                    <li><strong>Droit de rectification :</strong> corriger vos informations</li>
                    <li><strong>Droit à l'effacement :</strong> supprimer vos données</li>
                    <li><strong>Droit d'opposition :</strong> vous opposer au traitement</li>
                    <li><strong>Droit à la portabilité :</strong> récupérer vos données</li>
                  </ul>
                  <p>
                    Pour exercer ces droits, contactez-nous à <strong>contact@eaugratuite.io</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Cookies et Tracking
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Notre site utilise uniquement des cookies techniques nécessaires 
                    au fonctionnement du site (navigation, formulaires).
                  </p>
                  <p>
                    Nous n'utilisons pas de cookies publicitaires ou de tracking tiers 
                    sans votre consentement explicite.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="p-8 border-0 shadow-lg bg-gradient-to-r from-sky-50 to-emerald-50">
              <CardContent className="p-0 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Questions sur cette politique ?
                </h2>
                <p className="text-gray-600 mb-6">
                  Notre équipe est disponible pour répondre à toutes vos questions 
                  concernant le traitement de vos données personnelles.
                </p>
                <div className="space-y-2">
                  <p className="font-semibold text-gray-900">Eau Gratuite</p>
                  <p className="text-gray-600">Email : contact@eaugratuite.io</p>
                  <p className="text-gray-600">Téléphone : 07 49 97 72 32</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;